import { mat4 } from 'gl-matrix';
import { DomainContext } from '../../changes/DomainContext';
import { RegistrationId } from '../../changes/ids';
import { domainTrace } from '../../diagnostics/domainTrace';
import {
    ApprovedRegistrationError,
    DomainValidationError,
    StaleReferenceError,
} from '../../errors/DomainError';
import { Image3D } from '../../patient/image3d';
import { Patient } from '../../patient/patient';
import { ApprovableRegistration } from '../../patient/approvableRegistration';
import {
    isRigidRegistration,
    RigidRegistration,
} from '../../patient/rigidRegistration';
import { DicomProcessor } from '../dicomProcessor';

/** Lightweight identity passed from UI down to registration commands. */
export interface RegistrationRef {
    readonly id: RegistrationId;
}

export class RegistrationService {
    constructor(
        private readonly context: DomainContext,
        private readonly patient: Patient
    ) {}

    public rename(ref: RegistrationRef, label: string): void {
        domainTrace.service('RegistrationService.rename', {
            id: ref.id,
            label,
        });
        this.context.tracker.command(() => this.resolve(ref).rename(label));
    }

    public setDescription(ref: RegistrationRef, description: string): void {
        domainTrace.service('RegistrationService.setDescription', {
            id: ref.id,
        });
        this.context.tracker.command(() =>
            this.resolve(ref).setDescription(description)
        );
    }

    public approve(ref: RegistrationRef, reviewer: string): void {
        domainTrace.service('RegistrationService.approve', {
            id: ref.id,
            reviewer,
        });
        this.context.tracker.command(() => this.resolve(ref).approve(reviewer));
    }

    public reject(ref: RegistrationRef, reviewer: string): void {
        domainTrace.service('RegistrationService.reject', {
            id: ref.id,
            reviewer,
        });
        this.context.tracker.command(() => this.resolve(ref).reject(reviewer));
    }

    public unapprove(ref: RegistrationRef): void {
        domainTrace.service('RegistrationService.unapprove', { id: ref.id });
        this.context.tracker.command(() => this.resolve(ref).unapprove());
    }

    public setExported(ref: RegistrationRef): void {
        domainTrace.service('RegistrationService.setExported', { id: ref.id });
        this.context.tracker.command(() => this.resolve(ref).setExported());
    }

    /**
     * Records a committed matrix edit for dirty tracking. Driven by the
     * RegistrationEditBridge off {@link RegistrationMatrixCommittedEvent} —
     * never called per tool. Throws {@link ApprovedRegistrationError} if the
     * registration is approved (a bug in the engine guards; must surface).
     */
    public commitMatrixEdit(ref: RegistrationRef): void {
        domainTrace.service('RegistrationService.commitMatrixEdit', {
            id: ref.id,
        });
        this.context.tracker.command(() =>
            this.resolveRigid(ref).markMatrixEdited()
        );
    }

    public remove(ref: RegistrationRef): void {
        domainTrace.service('RegistrationService.remove', { id: ref.id });
        this.context.tracker.command(() => {
            const reg = this.resolve(ref);
            if (reg.isApproved) {
                throw new ApprovedRegistrationError(
                    `Cannot delete approved registration '${reg.contentLabel}'`
                );
            }
            this.patient.removeRegistration(reg);
        });
    }

    public create(
        primaryImage: Image3D,
        secondaryImage: Image3D,
        initialTransform: mat4 = mat4.create(),
        contentCreatorName: string = ''
    ): RigidRegistration | undefined {
        domainTrace.service('RegistrationService.create', {});
        if (
            primaryImage.frameOfReferenceUid ===
            secondaryImage.frameOfReferenceUid
        ) {
            return undefined;
        }
        const label = this.getNextRegistrationLabel();
        const dicomParent = DicomProcessor.createEmptyDicomRegistration(
            this.patient,
            primaryImage,
            secondaryImage,
            label,
            initialTransform,
            contentCreatorName
        );
        const reg = new RigidRegistration(
            primaryImage.frameOfReferenceUid,
            secondaryImage.frameOfReferenceUid,
            dicomParent.transformation,
            dicomParent
        );
        this.context.tracker.command(() => this.patient.addRegistration(reg));
        return reg;
    }

    /**
     * Creates a new registration that is the inverse of the registration
     * referenced by {@link ref}. The new registration swaps source and
     * target frames of reference and inverts the transformation matrix.
     *
     * Returns the new registration and the resolved images
     * (newPrimaryImage = old secondary, newSecondaryImage = old primary),
     * or undefined if the operation cannot be performed. Throws
     * {@link ApprovedRegistrationError} if the source registration is
     * approved.
     *
     * The whole operation (create + rename + setDescription) coalesces
     * into a single changeset so it can be undone/redone atomically.
     */
    public invert(
        ref: RegistrationRef,
        creator: string = ''
    ):
        | {
              registration: RigidRegistration;
              newPrimaryImage: Image3D;
              newSecondaryImage: Image3D;
          }
        | undefined {
        domainTrace.service('RegistrationService.invert', { id: ref.id });
        return this.context.tracker.batch(() => {
            const existing = this.resolveRigid(ref);
            if (existing.isApproved) {
                throw new ApprovedRegistrationError(
                    `Cannot invert approved registration '${existing.contentLabel}'`
                );
            }

            const oldPrimaryImage = this.patient.images.find(
                (i) =>
                    i.frameOfReferenceUid === existing.sourceFrameOfReferenceUid
            );
            const oldSecondaryImage = this.patient.images.find(
                (i) =>
                    i.frameOfReferenceUid === existing.targetFrameOfReferenceUid
            );
            if (!oldPrimaryImage || !oldSecondaryImage) {
                // No PHI: only ids and FOR uids are traced.
                domainTrace.service('RegistrationService.invert: aborted', {
                    reason: 'image-not-resolvable-by-for',
                    id: ref.id,
                    sourceFrameOfReferenceUid:
                        existing.sourceFrameOfReferenceUid,
                    targetFrameOfReferenceUid:
                        existing.targetFrameOfReferenceUid,
                    oldPrimaryImageResolved: Boolean(oldPrimaryImage),
                    oldSecondaryImageResolved: Boolean(oldSecondaryImage),
                });
                return undefined;
            }

            const invertedTransform = mat4.invert(
                mat4.create(),
                existing.transformation
            );
            if (!invertedTransform) {
                // No PHI: only ids and FOR uids are traced.
                domainTrace.service('RegistrationService.invert: aborted', {
                    reason: 'non-invertible-transform',
                    id: ref.id,
                    sourceFrameOfReferenceUid:
                        existing.sourceFrameOfReferenceUid,
                    targetFrameOfReferenceUid:
                        existing.targetFrameOfReferenceUid,
                });
                return undefined;
            }

            // Old secondary becomes new primary/source.
            const newReg = this.create(
                oldSecondaryImage,
                oldPrimaryImage,
                invertedTransform,
                creator
            );
            if (!newReg) {
                return undefined;
            }

            newReg.rename(this.getNextInvertedLabel());
            newReg.setDescription(
                `This registration is inverted from ${existing.contentLabel}`
            );

            return {
                registration: newReg,
                newPrimaryImage: oldSecondaryImage,
                newSecondaryImage: oldPrimaryImage,
            };
        });
    }

    private getNextRegistrationLabel(): string {
        return this.getNextLabelWithPrefix('REG');
    }

    private getNextInvertedLabel(): string {
        return this.getNextLabelWithPrefix('INV_REG');
    }

    private getNextLabelWithPrefix(prefix: string): string {
        let max = 0;
        for (const reg of this.patient.registrations) {
            const label = reg.contentLabel ?? '';
            if (!label.startsWith(prefix)) {
                continue;
            }
            const num = Number(label.slice(prefix.length));
            if (Number.isFinite(num) && num > max) {
                max = num;
            }
        }
        return `${prefix}${max + 1}`;
    }

    private resolve(ref: RegistrationRef): ApprovableRegistration {
        const reg = this.patient.approvableRegistrations.find(
            (r) => r.id === ref.id
        );
        if (!reg) {
            throw new StaleReferenceError(
                `Registration ${ref.id} no longer exists.`
            );
        }
        return reg;
    }

    private resolveRigid(ref: RegistrationRef): RigidRegistration {
        const reg = this.resolve(ref);
        if (!isRigidRegistration(reg)) {
            throw new DomainValidationError(
                `Registration '${reg.contentLabel}' is a deformation field and carries no transform matrix.`
            );
        }
        return reg;
    }
}
